import React from 'react';
import { styled } from '@mui/material/styles';
import { Button, CircularProgress, Tooltip, tooltipClasses, IconButton } from '@mui/material';
import { Share } from '@mui/icons-material';
import { EVENT_STATUS } from '../../../../app/store/constant.js';
import { H4, H6, Body3 } from '../../../../themes/typography';
import useEvent from '../../../../hooks/event-hooks/useEvent.js';

const HtmlTooltip = styled(({ className, ...props }) => (
    <Tooltip {...props} classes={{ popper: className }} />
))(() => ({
    [`& .${tooltipClasses.tooltip}`]: {
        backgroundColor: 'var(--quaternary-color)',
        color: 'var(--primary-color)',
        width: 220,
        display: 'flex',
        flexDirection: 'column',
        gap: '0.5rem'
    }
}));

function ToPublishEvent() {
    return (
        <React.Fragment>
            <H6 color="inherit">To publish your event</H6>
            <Body3 color="var(--primary-color)" fontWeight={400}>
                1. Make sure you have filled all the required fields of the event.
            </Body3>
            <Body3 color="var(--primary-color)" fontWeight={400}>
                2. Make sure you have created at least one ticket.
            </Body3>
        </React.Fragment>
    );
}

function EventAction({
    handlePublish,
    loading,
    isReadyToPublish,
    getEventStatus,
    shouldDisablePauseButton,
    shouldShowPublishButton,
    handlePauseEvent,
    shouldShowPauseButton,
    shouldShowDeleteButton,
    handleCancelEvent,
    handleDeleteEvent,
    changeEventStatusChipColor,
    handlePreviewEvent,
    whichActionBtnClicked,
    eventData
}) {

    const { isBasicDetailsCompleted } = useEvent()

    return (
        <div className="event-status-draft-container">
            <div className="event-name-with-draft-chip">
                <H4 color="var(--secondary-color)">{eventData?.title}</H4>
                {eventData?.status && (
                    <H6 color={changeEventStatusChipColor} fontWeight={400}>
                        {getEventStatus()}
                    </H6>
                )}
            </div>

            <div className="event-action-btns-container">
                {isReadyToPublish && (
                    <Button variant="pause" onClick={handlePreviewEvent}>
                        Preview
                    </Button>
                )}

                {shouldShowPublishButton &&
                    (!isReadyToPublish ? (
                        <HtmlTooltip title={<ToPublishEvent />}>
                            <span>
                                <Button variant="publish" disabled={!isReadyToPublish}>
                                    Publish
                                </Button>
                            </span>
                        </HtmlTooltip>
                    ) : (
                        <Button
                            variant="publish"
                            onClick={handlePublish}
                            disabled={loading && whichActionBtnClicked === 'publish'}
                        >
                            Publish{' '}
                            {loading && whichActionBtnClicked === 'publish' && (
                                <CircularProgress
                                    size={20}
                                    sx={{ marginLeft: '0.75rem', color: 'var(--primary-color)' }}
                                />
                            )}
                        </Button>
                    ))}

                {shouldShowPauseButton && (
                    <Button
                        variant="pause"
                        disabled={shouldDisablePauseButton || (loading && whichActionBtnClicked === 'pause')}
                        onClick={handlePauseEvent}
                    >
                        Pause{' '}
                        {loading && whichActionBtnClicked === 'pause' && (
                            <CircularProgress
                                size={20}
                                sx={{ marginLeft: '0.75rem', color: 'var(--tertiary-color)' }}
                            />
                        )}
                    </Button>
                )}

                {!shouldShowDeleteButton && (
                    <Button
                        variant="cancel"
                        onClick={handleCancelEvent}
                        disabled={eventData?.status === EVENT_STATUS.cancelled || (loading && whichActionBtnClicked === 'cancel')}
                    >
                        Cancel{' '}
                        {loading && whichActionBtnClicked === 'cancel' && (
                            <CircularProgress
                                size={20}
                                sx={{ marginLeft: '0.75rem', color: 'var(--error-secondary-color)' }}
                            />
                        )}
                    </Button>
                )}

                {shouldShowDeleteButton && isBasicDetailsCompleted && (
                    <Button
                        variant="cancel"
                        onClick={handleDeleteEvent}
                        disabled={loading && whichActionBtnClicked === 'delete'}
                    >
                        Delete{' '}
                        {loading && whichActionBtnClicked === 'delete' && (
                            <CircularProgress
                                size={20}
                                sx={{ marginLeft: '0.75rem', color: 'var(--error-secondary-color)' }}
                            />
                        )}
                    </Button>
                )}

                <IconButton color="lightSecondary">
                    <Share sx={{ fontSize: '1.25rem' }} />
                </IconButton>
            </div>
        </div>
    );
}

export default EventAction;